import React from 'react';
import { cn } from '../../design-system/cn';
import { SPORT_GLYPHS } from '../../assets/sports/SportGlyphs';
import { getSportIconId, getSportLabel, getSportTheme, listH2HSportThemes } from '../../constants/sportTheme';

const SIZES = {
  xs: 'h-3.5 w-3.5',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
};

/**
 * SportIcon — reusable sport category glyph (Sprint 12 WI-2).
 *
 * Renders a LockedIn sport icon tinted from `sportTheme` accent tokens.
 * No team logos. No lifecycle side effects. Pure presentation.
 *
 * @param {object} props
 * @param {string} [props.sport] — raw sport key (NBA, NFL, …)
 * @param {string} [props.iconId] — optional glyph id from view-model (overrides sport lookup)
 * @param {'xs'|'sm'|'md'} [props.size='sm']
 * @param {boolean} [props.showLabel=false] — optional text beside icon
 * @param {string} [props.className] — layout-only wrapper overrides
 * @param {boolean} [props.decorative=true] — when true, icon is aria-hidden
 * @param {string} [props.label] — override display label when showLabel is true
 */
export default function SportIcon({
  sport,
  iconId: iconIdProp,
  size = 'sm',
  showLabel = false,
  className,
  decorative = true,
  label,
}) {
  const theme = getSportTheme(sport);
  const iconId = iconIdProp ?? getSportIconId(sport);
  const Glyph = SPORT_GLYPHS[iconId] || SPORT_GLYPHS.generic;
  const displayLabel = label ?? getSportLabel(sport);
  const sizeClass = SIZES[size] || SIZES.sm;

  const iconEl = (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center', sizeClass, theme.accent.icon)}
      aria-hidden={decorative || showLabel ? true : undefined}
      data-sport-icon-id={iconId}
    >
      <Glyph className="h-full w-full" />
    </span>
  );

  if (!showLabel) {
    return (
      <span className={cn('inline-flex items-center', className)}>
        {iconEl}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {iconEl}
      <span className="text-[10px] font-black uppercase tracking-widest text-gray-300">
        {displayLabel}
      </span>
    </span>
  );
}

/** Render all H2H sport icons — useful for visual regression / Storybook-style demos. */
export function SportIconGallery({ size = 'md', className }) {
  const themes = listH2HSportThemes();
  return (
    <div className={cn('flex flex-wrap gap-3', className)} data-testid="sport-icon-gallery">
      {themes.map((theme) => (
        <SportIcon key={theme.key} sport={theme.key} size={size} showLabel />
      ))}
    </div>
  );
}
